import { useCallback, useEffect, useRef } from "react";

import { postTrainingBlockProgress } from "@/api/client";

interface VideoPlayerProps {
  src: string;
  blockId: string;
  token: string;
  title?: string | null;
  /** CHP-1013: false for a view-only reader (CM) — plays the video without
   * writing any watch-progress rows. */
  trackProgress?: boolean;
}

// How often the player reports watch-progress to the backend.
const REPORT_INTERVAL_MS = 10_000;

/**
 * HTML5 video player for the trainee module player with watch-time tracking
 * (RACCPE-6 viewer + RACCPE-14 completion engine).
 *
 * Plays an MP4 from a presigned MinIO URL (ADR-0016). It tracks the maximum
 * `currentTime` observed and POSTs that high-water mark every 10s, plus on
 * pause/ended, via `postTrainingBlockProgress`. The backend keeps the larger
 * value and flips the block complete at ≥95% of duration. Free scrubbing is
 * intentional (project_training_feature.md) — dragging to the end credits the
 * watch, an accepted limitation.
 *
 * Progress is best-effort: a failed POST is swallowed and retried on the next
 * tick (the value is re-derivable, never lost).
 */
export function VideoPlayer({
  src,
  blockId,
  token,
  title,
  trackProgress = true,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const maxWatchedRef = useRef(0);
  const reportedRef = useRef(0);

  const report = useCallback(() => {
    if (!trackProgress) return;
    const seconds = Math.floor(maxWatchedRef.current);
    if (seconds <= reportedRef.current) return;
    postTrainingBlockProgress(token, blockId, seconds)
      .then(() => {
        reportedRef.current = seconds;
      })
      .catch(() => {
        // Best-effort — leave reportedRef so the next tick retries this value.
      });
  }, [token, blockId, trackProgress]);

  const captureCurrentTime = useCallback(() => {
    const video = videoRef.current;
    if (video) maxWatchedRef.current = Math.max(maxWatchedRef.current, video.currentTime);
  }, []);

  // Reset the watch trackers when the block changes, so a reused player
  // instance never carries one block's progress into another.
  useEffect(() => {
    maxWatchedRef.current = 0;
    reportedRef.current = 0;
  }, [blockId]);

  useEffect(() => {
    const id = window.setInterval(() => {
      captureCurrentTime();
      report();
    }, REPORT_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [captureCurrentTime, report]);

  const reportNow = useCallback(() => {
    captureCurrentTime();
    report();
  }, [captureCurrentTime, report]);

  return (
    <video
      ref={videoRef}
      controls
      preload="metadata"
      src={src}
      aria-label={title ?? "Training video"}
      onTimeUpdate={captureCurrentTime}
      onPause={reportNow}
      onEnded={reportNow}
      style={{ width: "100%", borderRadius: 10, background: "#000", display: "block" }}
    >
      {/* No caption track is available for uploaded MP4s yet; element present
          to satisfy the media-has-caption a11y rule. */}
      <track kind="captions" />
    </video>
  );
}
